import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import authService from '../services/authService';
import './Admin.css';

const API_URL =
  process.env.REACT_APP_API_URL ||
  process.env.REACT_APP_BACKEND_URL ||
  (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:3001');

function Admin() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [recentBlocked, setRecentBlocked] = useState([]);

  useEffect(() => {
    // Check if user is authenticated
    if (!authService.isAuthenticated()) {
      navigate('/login');
      return;
    }

    fetchModerationData();
  }, [navigate]);

  const fetchModerationData = async () => {
    const token = authService.getToken();

    try {
      setLoading(true);
      setError(null);

      // Fetch stats
      const statsResponse = await fetch(`${API_URL}/admin/moderation/stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (statsResponse.status === 403) {
        setError('Access denied. You are not an administrator.');
        setLoading(false);
        return;
      }

      if (!statsResponse.ok) {
        throw new Error(`HTTP ${statsResponse.status}: ${statsResponse.statusText}`);
      }

      const statsData = await statsResponse.json();
      setStats(statsData);

      // Fetch recent blocked content
      const recentResponse = await fetch(`${API_URL}/admin/moderation/recent?limit=10`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (recentResponse.ok) {
        const recentData = await recentResponse.json();
        setRecentBlocked(recentData);
      }

      setLoading(false);
    } catch (err) {
      console.error('Error fetching moderation data:', err);
      setError(err.message);
      setLoading(false);
    }
  };

  const runTests = async () => {
    const token = authService.getToken();

    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/admin/moderation/test`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const results = await response.json();
        alert(
          `Tests completed!\n\nPassed: ${results.filter((r) => r.success).length}/${results.length}`
        );
        fetchModerationData(); // Refresh data
      } else {
        alert('Test failed. Check console for details.');
      }
    } catch (err) {
      alert(`Error running tests: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    const token = authService.getToken();
    const url = `${API_URL}/admin/moderation/export`;

    // Create download link
    const link = document.createElement('a');
    link.href = url;
    link.download = 'moderation_export.csv';
    link.style.display = 'none';

    // Add auth header via fetch and create blob
    fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((response) => {
        // Check if request succeeded before processing blob
        if (!response.ok) {
          throw new Error(`Export failed with status ${response.status}: ${response.statusText}`);
        }
        return response.blob();
      })
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      })
      .catch((err) => {
        alert(`Export failed: ${err.message}`);
      });
  };

  if (loading && !stats) {
    return (
      <div className="admin-container">
        <div className="admin-loading">Loading moderation dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-container">
        <div className="admin-error">
          <h2>Error</h2>
          <p>{error}</p>
          <button onClick={() => navigate('/')}>Go Home</button>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Content Moderation Dashboard</h1>
        <div className="admin-actions">
          <button onClick={runTests} disabled={loading}>
            Run Tests
          </button>
          <button onClick={exportCSV}>Export CSV</button>
          <button onClick={() => navigate('/')}>Home</button>
        </div>
      </div>

      {stats && (
        <>
          <div className="admin-status">
            <h2>System Status</h2>
            <div className="status-badge" data-mode={stats.mode}>
              {stats.mode === 'live' ? 'LIVE (Perspective API)' : 'MOCK MODE'}
            </div>
            <p className="status-threshold">
              Threat Threshold: {stats.thresholds.THREAT} | Hate Speech Threshold:{' '}
              {stats.thresholds.IDENTITY_ATTACK}
            </p>
          </div>

          <div className="admin-stats">
            <h2>Statistics (Last 30 Days)</h2>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value">{stats.stats.totalBlocked}</div>
                <div className="stat-label">Total Blocked</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats.stats.threats}</div>
                <div className="stat-label">Threats</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats.stats.hateSpeech}</div>
                <div className="stat-label">Hate Speech</div>
              </div>
              <div className="stat-card">
                <div className="stat-value">{stats.stats.severeAlerts}</div>
                <div className="stat-label">Severe Alerts</div>
              </div>
            </div>
          </div>

          <div className="admin-recent">
            <h2>Recent Blocked Content</h2>
            {recentBlocked.length === 0 ? (
              <p className="no-data">No blocked content yet</p>
            ) : (
              <div className="blocked-list">
                {recentBlocked.map((item, index) => (
                  <div key={index} className="blocked-item">
                    <div className="blocked-header">
                      <span className={`reason-badge ${item.reason}`}>
                        {item.reason === 'threat' ? 'Threat' : 'Hate Speech'}
                      </span>
                      <span className="blocked-time">
                        {new Date(item.blocked_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="blocked-text">{item.text}</div>
                    <div className="blocked-scores">
                      Threat: {(item.threat_score * 100).toFixed(1)}% | Hate Speech:{' '}
                      {(item.hate_speech_score * 100).toFixed(1)}%
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default Admin;
